// Pure Japan-coverage data for the market-coverage map.
//
// Like coverage.ts this module has NO DOM and NO Leaflet dependency, so it can
// be unit-tested in isolation. It owns:
//   1. the JEPX/MPX utility-region descriptors (name + whether KYOS covers it),
//   2. the bundled prefecture polygons (each tagged with its region) that the
//      Japan tile view draws,
//   3. the faded silhouette SVGs used by the corner inset preview.
//
// The prefecture polygons carry a `region` property (assigned offline by
// prefecture code), so the renderer styles each prefecture by its region
// without needing the prefecture→region mapping at runtime.

import europeThumbnailSvg from './data/europe-thumbnail.svg?raw'
import japanThumbnailSvg from './data/japan-thumbnail.svg?raw'
import japanPrefectures from './data/japan-prefectures.json'

/** Curve type surfaced for every covered Japan region in the tooltip. */
export const JAPAN_CURVE_TYPE_LABEL = 'Fundamental curves (MPX)' as const

/** Descriptor for one JEPX/MPX utility region. */
export interface JapanRegion {
    /** Human-readable JEPX region name shown in the UI. */
    name: string
    /** Whether KYOS covers this region (Okinawa is excluded). */
    covered: boolean
}

/**
 * JEPX/MPX utility-region code → descriptor. The codes match the `region`
 * property baked into the bundled prefecture polygons. All regions are covered
 * except Okinawa.
 */
export const JAPAN_REGIONS: Readonly<Record<string, JapanRegion>> = {
    hokkaido: { name: 'Hokkaido', covered: true },
    tohoku: { name: 'Tohoku', covered: true },
    tokyo: { name: 'Tokyo', covered: true },
    chubu: { name: 'Chubu', covered: true },
    hokuriku: { name: 'Hokuriku', covered: true },
    kansai: { name: 'Kansai', covered: true },
    chugoku: { name: 'Chugoku', covered: true },
    shikoku: { name: 'Shikoku', covered: true },
    kyushu: { name: 'Kyushu', covered: true },
    okinawa: { name: 'Okinawa', covered: false },
}

/**
 * Resolve the descriptor for a region code (e.g. a prefecture's `region`
 * property). Returns `undefined` for unknown codes — never throws.
 */
export function regionInfo(code: string): JapanRegion | undefined {
    return JAPAN_REGIONS[code]
}

// Bundled (no runtime fetch): prefecture polygons for the tile view, and the
// faded silhouettes the corner inset previews.
export { japanPrefectures, europeThumbnailSvg, japanThumbnailSvg }
